<script lang="ts">
	import { untrack } from 'svelte'
	import { Button, ButtonSize, ButtonType, Icon } from '@repo/commons/components'

	interface Props {
		maxSize?: number
		allowedTypes?: string[]
		multiple?: boolean
		onFileSelect?: (files: File[]) => void
		onError?: (error: string) => void
	}

	let {
		maxSize = 5 * 1024 * 1024, // 5MB default
		allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'],
		multiple = true,
		onFileSelect = () => {},
		onError = () => {}
	}: Props = $props()

	let isDragOver = $state(false)

	let fileInput = $state<HTMLInputElement | null>(null)

	function formatFileSize(bytes: number): string {
		if (bytes === 0) return '0 Bytes'
		const k = 1024
		const sizes = ['Bytes', 'KB', 'MB', 'GB']
		const i = Math.floor(Math.log(bytes) / Math.log(k))
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
	}

	function getFileTypeDisplay(mimeType: string): string {
		const typeMap: Record<string, string> = {
			'application/pdf': 'PDF',
			'image/jpeg': 'JPEG',
			'image/png': 'PNG'
		}
		return typeMap[mimeType] || mimeType.split('/')[1].toUpperCase()
	}

	function validateFile(file: File): string | null {
		if (file.size > maxSize) {
			return `File size exceeds ${formatFileSize(maxSize)} limit`
		}

		if (!allowedTypes.includes(file.type)) {
			const allowedTypeNames = allowedTypes.map((type) => getFileTypeDisplay(type))
			return `File type not allowed. Allowed types: ${allowedTypeNames.join(', ')}`
		}

		return null
	}

	function handleFiles(fileList: FileList): void {
		const newFiles = Array.from(fileList)

		for (const file of newFiles) {
			const validationError = validateFile(file)
			if (validationError) {
				if (onError) onError(validationError)
				return
			}
		}

		if (onFileSelect) {
			untrack(() => onFileSelect(newFiles))
		}
	}

	function handleDragOver(event: DragEvent): void {
		event.preventDefault()
		isDragOver = true
	}

	function handleDragLeave(event: DragEvent): void {
		event.preventDefault()
		if (
			!event.relatedTarget ||
			!(event.currentTarget as HTMLElement).contains(event.relatedTarget as Node)
		) {
			isDragOver = false
		}
	}

	function handleDrop(event: DragEvent): void {
		event.preventDefault()
		isDragOver = false

		const droppedFiles = event.dataTransfer?.files
		if (droppedFiles && droppedFiles.length > 0) {
			handleFiles(droppedFiles)
		}
	}

	function handleFileInput(event: Event): void {
		const target = event.target as HTMLInputElement
		const selectedFiles = target.files
		if (selectedFiles && selectedFiles.length > 0) {
			handleFiles(selectedFiles)
		}
	}

	function browseFiles(): void {
		if (fileInput) {
			fileInput.value = ''
			fileInput.click()
		}
	}

	const allowedTypesDisplay = $derived(
		allowedTypes.map((type) => getFileTypeDisplay(type)).join(', ')
	)
</script>

<div class="file-upload-container">
	<input
		bind:this={fileInput}
		type="file"
		class="hidden-input"
		{multiple}
		accept={allowedTypes.join(',')}
		onchange={handleFileInput}
	/>

	<div
		class="drop-zone"
		class:drag-over={isDragOver}
		ondragover={handleDragOver}
		ondragleave={handleDragLeave}
		ondrop={handleDrop}
		role="button"
		tabindex="0"
		onclick={browseFiles}
		onkeydown={(e) => e.key === 'Enter' && browseFiles()}
	>
		<!-- Upload icon -->
		<div class="upload-icon">
			<Icon name="upload" />
		</div>

		<div class="upload-text">
			<h3>Drag and drop files here</h3>
			<p>
				Maximum file size is {formatFileSize(maxSize)}. Allowed file types: {allowedTypesDisplay}
			</p>
		</div>

		<div class="browse-btn-wrapper">
			<Button
				buttonText="Browse files"
				buttonType={ButtonType.NEUTRAL}
				buttonSize={ButtonSize.SMALL}
				onclick={browseFiles}
			/>
		</div>
	</div>
</div>

<style>
	.file-upload-container {
		@apply w-full;
	}

	.hidden-input {
		@apply absolute opacity-0 pointer-events-none;
	}

	.drop-zone {
		@apply border border-dashed border-[#002C4D80] rounded-xl p-8 text-center cursor-pointer transition-colors duration-200 bg-[#00498006];
	}

	.drop-zone:hover {
		@apply border-[#007ACCCC] bg-[#007ACC0D];
	}

	.drop-zone.drag-over {
		@apply border-[#007ACC] bg-[#007ACC14];
	}

	.upload-icon {
		@apply inline-flex items-center justify-center w-12 h-12 bg-[#007ACC0D] text-[#007ACCCC] rounded-full mb-4 mx-auto;
	}

	.upload-text h3 {
		@apply text-base font-medium text-[#001626E5] mb-1;
	}

	.upload-text p {
		@apply text-sm text-[#001D33B2] mb-5;
	}

	.browse-btn-wrapper {
		@apply flex w-full justify-center;
	}
</style>
